import { AudioOutputSettings, AudioSourceInfo } from '../../UserContext'
import { isSubset, fillSparseArray } from '../../utils'
import { AudioEngineOutputDeviceType, AudioEngineLocalStreamStats, AudioEngineRemoteStreamStats } from './types'
import { IAudioEngine } from './IAudioEngine'
import { RemoteStatsSummaryBuilder } from './RemoteStatsSummaryBuilder'
import { getAudioEngineInstance } from '.'

export type EnabledPerChannelGroup = { channelGroup: number[]; value: boolean }[]
export type VolumePerChannelGroup = Array<{ channelGroup: number[]; value: number }>
export type StreamSettingsChangedHandlerType = (data: { isMuted?: boolean[]; volume?: number[] }) => void

export type FakeMediaStreamTrackConstructorParams = {
    id: string
    label: string
    channelSelection: number[][]
    channelUnpackingMap: number[]
    localTrackStopHandler?: () => void
    streamSettingsChangedHandler?: StreamSettingsChangedHandlerType
}

export default class FakeMediaStreamTrack {
    audioEngine: IAudioEngine

    enabled: EnabledPerChannelGroup
    volume: VolumePerChannelGroup
    remoteStats: AudioEngineRemoteStreamStats
    remoteStatsSummary: RemoteStatsSummaryBuilder = new RemoteStatsSummaryBuilder()
    localStats: AudioEngineLocalStreamStats
    startedTime: Date = new Date()

    id: string
    kind: 'audio' = 'audio'
    label: string
    channelSelection: number[][]
    channelUnpackingMap: number[]

    localTrackStopHandler: (() => void) | null
    streamSettingsChangedHandler: StreamSettingsChangedHandlerType | null

    // audio that is being currently outputted.
    currOutputVolumePerChannel: number[] = []

    evListeners: Map<string, any[]> = new Map<string, any[]>()

    constructor(params: FakeMediaStreamTrackConstructorParams) {
        this.enabled = params.channelSelection.map((chgrp) => ({ channelGroup: chgrp, value: true }))
        this.volume = params.channelSelection.map((chgrp) => ({ channelGroup: chgrp, value: 1.0 }))
        this.remoteStats = new AudioEngineRemoteStreamStats()
        this.localStats = new AudioEngineLocalStreamStats()
        this.id = params.id
        this.label = params.label
        this.channelSelection = params.channelSelection
        this.channelUnpackingMap = params.channelUnpackingMap
        this.localTrackStopHandler = params.localTrackStopHandler || null
        this.streamSettingsChangedHandler = params.streamSettingsChangedHandler || null
        this.audioEngine = getAudioEngineInstance()
    }

    getCapabilities() {
        return {}
    }

    getConstraints() {
        return {}
    }

    getSettings() {
        return {}
    }

    // 'ended', 'onStreamUnresponsive', 'onSampleRateMismatch'
    addEventListener(eventName: string, handler: any) {
        if (!this.evListeners.has(eventName)) {
            this.evListeners.set(eventName, [])
        }
        this.evListeners.get(eventName)?.push(handler)
    }

    stop() {
        if (this.localTrackStopHandler) {
            this?.localTrackStopHandler()
        }
    }

    setEnabled(isEnabled: EnabledPerChannelGroup) {
        // convert per-channel group to internal channel array.
        let iarr: boolean[] = []
        isEnabled.forEach((item) => {
            item.channelGroup.forEach((ch) => {
                iarr[ch] = !item.value
            })
        })
        iarr = fillSparseArray(iarr, true)

        if (this.streamSettingsChangedHandler) {
            this?.streamSettingsChangedHandler({ isMuted: iarr })
        }
        this.enabled = isEnabled
    }

    setEnabledOfChannelGroup(value: boolean, chgrp: number[]) {
        const next: EnabledPerChannelGroup = JSON.parse(JSON.stringify(this.enabled))
        next.forEach((v) => {
            if (JSON.stringify(v.channelGroup) === JSON.stringify(chgrp)) {
                v.value = value
            }
        })
        this.setEnabled(next)
    }

    getEnabledOfChannelGroup(chgrp: number[]) {
        for (let v of this.enabled) {
            if (isSubset(chgrp, v.channelGroup)) {
                return v.value
            }
        }

        return false
    }

    // v: volume for each channel group.
    setVolume(vs: VolumePerChannelGroup) {
        // convert per-channel group to internal channel array.
        let iarr: number[] = []
        vs.forEach((item) => {
            item.channelGroup.forEach((ch) => {
                iarr[ch] = Math.round(item.value * 1000)
            })
        })
        iarr = fillSparseArray(iarr, 1)

        if (this.streamSettingsChangedHandler) {
            this?.streamSettingsChangedHandler({ volume: iarr })
        }
        this.volume = vs
    }

    setVolumeOfChannelGroup(value: number, chgrp: number[]) {
        const nextVolume: VolumePerChannelGroup = JSON.parse(JSON.stringify(this.volume))
        nextVolume.forEach((v) => {
            if (JSON.stringify(v.channelGroup) === JSON.stringify(chgrp)) {
                v.value = value
            }
        })
        this.setVolume(nextVolume)
    }

    getCurrentOutputVolume(chgrp: number[]) {
        let out: number[] = []
        chgrp.forEach((ch) => {
            const ich = this.channelUnpackingMap[ch]
            if (ich >= 0) {
                out.push(this.currOutputVolumePerChannel[ich] || 0)
            }
        })
        return out
    }

    startRemoteRecording(
        outputFileFullPath: string | null,
        source: AudioSourceInfo | null,
        settings: AudioOutputSettings | null
    ) {
        this.audioEngine.startRemoteRecording(this.id, null, outputFileFullPath, source, settings)
    }

    stopRemoteRecording() {
        this.audioEngine.stopRemoteRecording(this.id, null)
    }

    startLocalRecording(outputFileFullPath: string) {
        // open output device `AudioEngineOutputDeviceType.FILE_LOCAL_LOOPBACK`.
        const source2 = {
            audioEngineDeviceId: 'file://' + outputFileFullPath,
        }
        const settings2: AudioOutputSettings = {
            sampleRate: 48000,
            bitDepth: 16,
            channelCount: 2,
            compressionBitrate: 256000,
            isSelective: true,
            includeStreams: [this.id],
            excludeStreams: [],
        }
        /* @ts-ignore */
        this.audioEngine.setAudioOutputDevice(source2, settings2, AudioEngineOutputDeviceType.FILE_LOCAL_LOOPBACK)

        // create local loopback
        this.audioEngine.setAudioLoopbackStream(this, AudioEngineOutputDeviceType.FILE_LOCAL_LOOPBACK)
    }

    stopLocalRecording() {
        this.audioEngine.setAudioOutputDevice(null, null, AudioEngineOutputDeviceType.FILE_LOCAL_LOOPBACK)
    }

    internalSetCurrentRemoteStats(s: AudioEngineRemoteStreamStats) {
        this.currOutputVolumePerChannel = s.volume
        this.remoteStats = s

        this.remoteStatsSummary.insertDataPoint(s)
    }

    internalSetCurrentLocalStats(s: AudioEngineLocalStreamStats) {
        if (this.localStats.sampleRateMismatchWarning === false && s.sampleRateMismatchWarning === true) {
            // sampleRateMismatchWarning turned true
            const handlers = this.evListeners.get('onSampleRateMismatch') || []
            handlers.forEach((h) => {
                h(s)
            })
        }

        if (this.localStats.streamUnresponsiveWarning === false && s.streamUnresponsiveWarning === true) {
            const handlers = this.evListeners.get('onStreamUnresponsive') || []
            handlers.forEach((h) => {
                h(s)
            })
        }

        this.currOutputVolumePerChannel = s.volume
        this.localStats = s
    }
}
